<template>
  <div class="volunteers-management">
    <!-- Enhanced Header Section -->
    <div class="management-header">
      <v-container fluid class="header-container">
        <div class="header-content">
          <div class="title-section">
            <h1 class="page-title">
              <v-icon large class="title-icon">mdi-account-group</v-icon>
              {{ $t("agencies") }}
            </h1> 
            <p class="page-subtitle">Manage and track agency activities</p>
          </div>
        
          <!-- Advanced Filter Controls -->
          <div class="header-controls">
            <div class="search-container">
              <v-text-field
                v-model="searchQuery"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search agencies..."
                solo
                flat
                hide-details
                dense
                class="search-field"
                clearable
              />
            </div>
          
            <div class="filter-group">
              <v-select
                v-model="selectedActiveAgencies"
                :items="activeAgenciesFilters"
                item-text="text"
                item-value="value"
                label="Active Agencies"
                outlined
                dense
                hide-details
                class="filter-select"
              >
                <template v-slot:label>
                  <span style="color: black; background: white; padding: 2px 8px; border-radius: 6px;">Active Agencies</span>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-account-check</v-icon>
                </template>
              </v-select>

              <v-select
                v-model="selectedStatus"
                :items="statusFilters"
                item-text="text"
                item-value="value"
                label="Status"
                outlined
                dense
                hide-details
                clearable
                class="filter-select"
              >
              <template v-slot:label>
                  <span style="color: black; background: white; padding: 2px 8px; border-radius: 6px;">Status</span>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-check-circle</v-icon>
                </template>
              </v-select>
            </div>
          
            <div class="action-buttons">
              <v-btn
                color="success"
                dark
                @click="exportData"
                class="action-btn"
                elevation="0"
              >
                <v-icon left small>mdi-download</v-icon>
                Export
              </v-btn>
            
              <v-btn
                color="primary"
                dark
                @click="refreshData"
                class="action-btn"
                elevation="0"
                :loading="loading"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <!-- Main Content Area -->
    <v-container fluid class="content-container">
      <!-- View Controls -->
      <div class="view-controls">
        <div class="results-info">
          <span class="results-text">
            Showing {{ filteredAgencies.length }} of {{ totalAgencies }} agencies
          </span>
        </div>
                <div class="view-toggle">
          <v-btn-toggle
            v-model="viewMode"
            mandatory
            borderless
            dense
            class="toggle-group"
          >
            <v-btn value="grid" small>
              <v-icon small>mdi-view-grid</v-icon>
              <span class="ml-1">Grid</span>
            </v-btn>
            <v-btn value="table" small>
              <v-icon small>mdi-table</v-icon>
              <span class="ml-1">Table</span>
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

         <!-- Loading State -->
          <div v-if="loading" class="skeleton-grid">
            <v-row dense>
              <v-col
                v-for="n in 6"
                :key="n"
                cols="12"
                sm="6"
                md="4"
              >
                <v-skeleton-loader
                  type="card"
                  class="mx-auto"
                  max-width="350"
                />
              </v-col>
            </v-row>
          </div>


            <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="volunteer-grid-container">
        <v-row v-if="filteredAgencies.length > 0" class="volunteer-grid">
          <v-col
            v-for="agency in filteredAgencies"
            :key="agency._id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="3"
          >
            <v-card
              class="volunteer-card"
              elevation="2"
              @click="goToProfile(agency)"
              :ripple="true"
            >
              <!-- Card Header with Background -->
              <div class="card-header">
                <div
                  class="header-background"
                  :style="{
                    backgroundImage: `url(${
                      agency.bgImage || require('@/assets/background0.png')
                    })`,
                  }"
                >
                  <div class="header-overlay"></div>
                  <div class="status-badge">
                    <v-chip
                      small
                      :color="getStatusColor(agency)"
                      dark
                      class="status-chip"
                    >
                      <v-icon left x-small>mdi-circle</v-icon>
                      {{ agency.status || "Active" }}
                    </v-chip>
                  </div>
                </div>

                <!-- Profile Avatar -->
                <div class="avatar-container">
                  <editable-avatar
                    :image="agency.thumbnail || agency.image"
                    :editable="false"
                    :size="80"
                    class="profile-avtar"
                  />
                  <div class="verification-badges">
                    <v-icon
                      v-if="agency.phone_verified"
                      small
                      color="success"
                      class="verification-icon"
                    >
                      mdi-check-decagram
                    </v-icon>
                    <v-icon
                      v-if="agency.email_verified"
                      small
                      color="success"
                      class="verification-icon"
                    >
                      mdi-email-check
                    </v-icon>
                  </div>
                </div>
              </div>

              <!-- Card Content -->
              <v-card-title class="volunteer-name">
                {{ agency.name }}
              </v-card-title>

              <v-card-subtitle class="volunteer-contact">
                <div class="contact-item">
                  <v-icon small class="contact-icon">mdi-phone</v-icon>
                  <span class="contact-text">{{
                    formatPhoneNumber(agency.phone)
                  }}</span>
                </div>
                <div class="contact-item">
                  <v-icon small class="contact-icon">mdi-email</v-icon>
                  <span class="contact-text">{{ agency.email }}</span>
                </div>
              </v-card-subtitle>

              <v-card-text class="card-body">
                <!-- Stats Row -->
                <div class="stats-row">
                   <div class="stat-item">
                    <v-icon small class="stat-icon">mdi mdi-account-heart</v-icon>
                    <div class="stat-content">
                      <div class="stat-value">
                        {{ agency.fanned?.length || 0 }}
                      </div>
                      <div class="stat-label">Fans</div>
                    </div>
                  </div>
                  <div class="stat-item">
                    <v-icon small class="stat-icon"
                      >mdi-account-multiple</v-icon
                    >
                    <div class="stat-content">
                      <div class="stat-value">
                        {{ agency.followed_by?.length || 0 }}
                      </div>
                      <div class="stat-label">
                        {{
                          (agency.followed_by?.length || 0) === 1
                            ? "Follower"
                            : "Followers"
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="stat-item">
                    <v-icon small class="stat-icon">mdi-heart</v-icon>
                    <div class="stat-content">
                      <div class="stat-value">
                        {{ agency.followers?.length || 0 }}
                      </div>
                      <div class="stat-label">Following</div>
                    </div>
                  </div>
                </div>

                <!-- Interests and Causes Section -->
                <div class="tags-container">
                  <!-- Causes -->
                  <div
                    v-if="agency.causes && agency.causes.length > 0"
                    class="tag-section causes-section"
                  >
                    <div class="section-header">
                      <v-icon small class="section-icon causes-icon"
                        >mdi-heart-multiple</v-icon
                      >
                      <span class="section-title">Causes</span>
                    </div>
                    <div class="tag-chips">
                      <v-chip
                        v-for="(cause, index) in agency.causes.slice(0, 2)"
                        :key="`cause-${index}`"
                        x-small
                        color="teal"
                        dark
                        class="cause-tag"
                      >
                        <v-icon x-small left>mdi-handshake</v-icon>
                        {{ cause }}
                      </v-chip>
                      <v-chip
                        v-if="agency.causes.length > 2"
                        x-small
                        color="teal lighten-2"
                        dark
                        class="cause-tag overflow-tag"
                      >
                        +{{ agency.causes.length - 2 }} more
                      </v-chip>
                    </div>
                  </div>

                  <!-- Empty state when no interests or causes -->
                  <div
                    v-if="
                      (!agency.interests ||
                        agency.interests.length === 0) &&
                      (!agency.causes || agency.causes.length === 0)
                    "
                    class="empty-tags"
                  >
                    <v-icon small color="grey lighten-1"
                      >mdi-information-outline</v-icon
                    >
                    <span class="empty-text"
                      >No interests or causes specified</span
                    >
                  </div>
                </div>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions class="card-actions">
                <v-btn
                  icon
                  color="primary"
                  @click.stop="onEditAgency(agency)"
                  class="action-icon"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  color="error"
                  @click.stop="onDeleteAgency(agency)"
                  class="action-icon"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
                <v-btn
                    icon
                    small
                    color="primary"
                    @click.stop="onUpdateAgencyStatus(agency)">
                    <v-icon small>
                      {{ agency.status == "Available" ? "mdi-close" : "mdi-check" }}
                    </v-icon>
                </v-btn>
                <v-spacer></v-spacer>

                <v-btn
                  small
                  color="primary"
                  @click.stop="goToProfile(agency)"
                  class="profile-btn"
                  elevation="0"
                >
                  View Profile
                  <v-icon right small>mdi-arrow-right</v-icon>
                </v-btn>
                
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State for Grid -->
        <div v-else class="empty-state">
          <v-icon size="64" color="grey lighten-1">mdi-account-search</v-icon>
          <h3 class="empty-title">No agencies found</h3>
          <p class="empty-subtitle">
            Try adjusting your search criteria or filters
          </p>
          <v-btn color="primary" @click="clearFilters" outlined>
            Clear Filters
          </v-btn>
        </div>
      </div>

      <!-- Table View -->
      <div class="table-container" v-else-if="viewMode === 'table'">
        <v-data-table
          :headers="tableHeaders"
          :items="filteredAgencies"
          :items-per-page="10"
          :loading="loading"
          :search="searchQuery"
          class="volunteer-table"
          elevation="2"
          @click:row="goToProfile"
        >
          <!-- Avatar Column -->
          <template v-slot:item.image="{ item }">
            <div class="table-avatar">
              <editable-avatar
                :image="item.thumbnail || item.image"
                :editable="false"
                :size="40"
              />
            </div>
          </template>

          <!-- Info Column -->
          <template v-slot:item.info="{ item }">
            <div class="volunteer-info clickable-info" @click="goToProfile(item)">
              <div class="volunteer-name-table">
                {{ item.name }}
                <div class="verification-indicators">
                  <v-icon v-if="item.phone_verified" x-small color="success">mdi-check-decagram</v-icon>
                  <v-icon v-if="item.email_verified" x-small color="success">mdi-email-check</v-icon>
                </div>
              </div>
              <div class="volunteer-contact-table">
                <div>{{ formatPhoneNumber(item.phone) }}</div>
                <div>{{ item.email }}</div>
              </div>
            </div>
          </template>

          <!-- Causes Column -->
          <template v-slot:item.causes="{ item }">
            <div class="causes-display">
              <v-chip
                v-for="(cause, index) in item.causes.slice(0, 2)"
                :key="index"
                x-small
                color="teal"
                dark
                class="mr-1 mb-1"
              >
                <v-icon x-small left>mdi-handshake</v-icon>
                {{ cause }}
              </v-chip>
              <v-chip
                v-if="item.causes.length > 2"
                x-small
                color="teal lighten-2"
                dark
              >
                +{{ item.causes.length - 2 }} more
              </v-chip>
            </div>
          </template>

          <!-- Address Column -->
          <template v-slot:item.address="{ item }">
            <div>
              {{ item.address }}{{ item.address2 ? ', ' + item.address2 : '' }}
              <div>{{ item.city }}, {{ item.state }} {{ item.zip }}</div>
            </div>
          </template>

          <!-- Followers Column -->
          <template v-slot:item.followers="{ item }">
            <div class="followers-display">
              <div>Fanned by {{ item.followed_by.length }} volunteers</div>
              <div>Followed by {{ item.followers.length }} agencies</div>
            </div>
          </template>

          <!-- About Column -->
          <template v-slot:item.about="{ item }">
            <div class="about-display">
              <div class="app-bold-font font-12">Who we are</div>
              <div class="text-block">{{ item.who_we_are }}</div>
              <div class="app-bold-font font-12">What we do</div>
              <div class="text-block">{{ item.what_we_do }}</div>
            </div>
          </template>

          <!-- Last Activity Column -->
          <template v-slot:item.lastActivity="{ item }">
            <div class="last-activity-display">
              <v-icon small color="primary" class="mr-1">mdi-history</v-icon>
              <span class="activity-value">
                {{ item.lastActivity === -1 ? 'No' : `${item.lastActivity || 0} days` }}
              </span>
            </div>
          </template>

          <!-- Created Date Column -->
          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <!-- Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="getStatusColor(item)"
              dark
            >
              {{ item.status }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="table-actions">
              <v-btn
                icon
                small
                color="primary"
                @click.stop="onUpdateAgencyStatus(item)"
              >
                <v-icon small>
                  {{ item.status == "Available" ? "mdi-close" : "mdi-check" }}
                </v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                @click.stop="onEditAgency(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click.stop="onDeleteAgency(item)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-container>

    <!-- Dialogs -->
    <confirm-delete-dialog
      :deleteDialog="deleteDialog"
      :onClose="onCloseConfirmDelete"
      :onConfirm="onConfirmDelete"
    />

    <!-- Floating Action Button -->
    <v-fab-transition>
      <v-btn
        fab
        fixed
        bottom
        right
        color="primary"
        class="fab-button"
        @click="scrollToTop"
      >
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
    </v-fab-transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import dateFormat from "dateformat";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";

export default {
  name: "AgenciesManagement",
  components: {
    EditableAvatar,
    ConfirmDeleteDialog
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      selectedStatus: null,
      selectedActiveAgencies: 'all',
      deleteDialog: false,
      selected: null,
      viewMode: "table",
      activeAgenciesFilters: [
        { text: 'All', value: 'all' },
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' }
      ],
      statusFilters: [
        { text: 'All', value: null },
        { text: 'Available', value: 'Available' },
        { text: 'Pending', value: 'Pending' },
        { text: 'Rejected', value: 'Rejected' }
      ],
      
      tableHeaders: [
        { text: "", value: "image", sortable: false, width: "60px" },
        { text: "Agency Info", value: "info", sortable: true, width: "180px" },
        { text: "Causes", value: "causes", sortable: true },
        { text: "Address", value: "address", sortable: true },
        // { text: "Followers", value: "followers", sortable: true },
        // { text: "Following", value: "followers", sortable: true },
        { text: "Last Activity In", value: "lastActivity", sortable: true, width: "150px" },
        { text: "Created", value: "createdAt", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "120px" }
      ]
    };
  },
  computed: {
    ...mapGetters({
      agencies: "agency/getAdminAgencies",
      profile: "auth/getProfile",
      profileType: "auth/getType",
      timezone: "usersetting/getTimeZone"
    }),

    filteredAgencies() {
      let agencies = [...(this.agencies || [])];
      
      // Filter by active agencies
      if (this.selectedActiveAgencies === 'yes') {
        agencies = agencies.filter(a => a.lastActivity !== -1);
      } else if (this.selectedActiveAgencies === 'no') {
        agencies = agencies.filter(a => a.lastActivity === -1);
      }
      // 'all' shows everything, no filter needed
      
      // Filter by status
      if (this.selectedStatus) {
        agencies = agencies.filter(a => a.status === this.selectedStatus);
      }
      
      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        agencies = agencies.filter(a =>
          a.name.toLowerCase().includes(query) ||
          (a.email || '').toLowerCase().includes(query) ||
          (a.phone || '').toLowerCase().includes(query) ||
          (a.causes || []).some(c => c.toLowerCase().includes(query))
        );
      }
      
      return agencies;
    },

    totalAgencies() {
      return this.agencies?.length || 0;
    }
  },
  methods: {
    ...mapActions("agency", {
      fetchAllAgencies: "fetchAllAdminAgencies",
      updateAgencyStatus: "updateAgencyStatus",
      deleteAgency: "deleteAgency"
    }),

    refreshData() {
      this.loading = true;
      const payload = {
        type: this.$route.query.type || "all"
      };
      this.fetchAllAgencies(payload)
        .then(() => {
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          console.error(error);
        });
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    goToProfile(agency) {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: agency._id }
      });
    },

    // onUpdateAgencyStatus(item) {
    //   this.loading = true;
    //   this.updateAgencyStatus({
    //     _id: item._id,
    //     status:
    //       item.status == "Pending" || item.status == "Rejected"
    //         ? "Available"
    //         : "Rejected"
    //   })
    //     .then(() => {
    //       this.loading = false;
    //     })
    //     .catch(error => {
    //       this.loading = false;
    //       console.log(error);
    //     });
    // },
    onUpdateAgencyStatus(item) {
  this.loading = true;
  const newStatus =
    item.status == "Pending" || item.status == "Rejected"
      ? "Available"
      : "Rejected";
  this.updateAgencyStatus({
    _id: item._id,
    status: newStatus
  })
    .then(() => {
      // Update the status locally for instant UI feedback
      const agency = this.agencies.find(a => a._id === item._id);
      if (agency) {
        agency.status = newStatus;
      }
      this.loading = false;
    })
    .catch(error => {
      this.loading = false;
      console.log(error);
    });
},

    onEditAgency(item) {
      this.selected = item;
      // this.editDialog = true;
      this.$router.push({
        name: "edit-agency",
        params: { id: item._id }
      });
    },
    clearFilters() {
      this.searchQuery = "";
      this.selectedAgeGroup = null;
      this.selectedStatus = null;
      this.selectedActiveAgencies = 'all';
    },
    onDeleteAgency(item) {
      this.selected = item;
      this.deleteDialog = true;
    },

    onCloseConfirmDelete() {
      this.deleteDialog = false;
    },

    onConfirmDelete() {
      this.deleteDialog = false;
      this.loading = true;
      this.deleteAgency({ _id: this.selected._id })
        .then(() => {
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          console.log(error);
        });
    },

    formatDate(date) {
      if (!date) return '';
      return dateFormat(this.convertTZ(date, this.timezone), "mm/dd/yyyy");
    },

    formatPhoneNumber(str) {
      if (!str) return '';
      const cleaned = ("" + str).replace(/\D/g, "");
      const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        const intlCode = match[1] ? "+1 " : "";
        return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
      }
      return str;
    },

    convertTZ(date, tzString) {
      if (!date) return "";
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", {
          timeZone: tzString || "America/New_York"
        })
      );
    },

    getStatusColor(agency) {
      const status = agency.status;
      switch(status) {
        case 'Available': return 'success';
        case 'Rejected': return 'error';
        case 'Pending': return 'warning';
        default: return 'info';
      }
    },

    exportData() {
      console.log('Exporting agency data');
      this.$toast.info("Export functionality coming soon");
    }
  },
  mounted() {
    if (this.profileType != "Admin") {
      this.$router.push({ name: "home" });
      return;
    }
    
    // Check if there's an activeAgencies query parameter
    if (this.$route.query.activeAgencies) {
      this.selectedActiveAgencies = this.$route.query.activeAgencies;
    }
    
    this.refreshData();
  }
};
</script>


<style scoped lang="scss">
.volunteers-management {
  // min-height: 100vh;
  // background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  // padding: 0px 608px;
  width: 100vw;
}

// Header Styles
.management-header {
  background: linear-gradient(135deg, #0a5b8a 0%, #0d8abc 100%);
  padding: 2rem 0;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 24px 24px;

  .header-container {
    max-width: 1400px;
  }

  .header-content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .title-section {
    .page-title {
      color: white;
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 1rem;

      .title-icon {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        padding: 8px;
      }
    }

    .page-subtitle {
      color: rgba(255, 255, 255, 0.8);
      font-size: 1.1rem;
      margin: 0;
    }
  }

  .header-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;

    .search-container {
      flex: 1;
      min-width: 300px;
      

      .search-field {
        background: rgba(255, 255, 255, 0.95);
        border-radius: 12px;
        backdrop-filter: blur(10px);

        ::v-deep .v-input__slot {
          box-shadow: none !important;
        }
      }
    }

    .filter-group {
      display: flex;
      gap: 1rem;

      .filter-select {
        min-width: 180px;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 12px;
        backdrop-filter: blur(10px);

        ::v-deep .v-input__slot {
          box-shadow: none !important;
        }
      }
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;

      .action-btn {
        border-radius: 12px;
        text-transform: none;
        font-weight: 600;
        backdrop-filter: blur(10px);
      }
    }
  }
}

// Content Styles
.content-container {
  max-width: 1400px;
  margin: 0 auto;
}

// Stats Overview
.stats-overview {
  margin-bottom: 2rem;

  .stat-card {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.9);
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
    }

    .stat-content {
      display: flex;
      align-items: center;
      padding: 1.5rem;
      gap: 1rem;

      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        &.success {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }
        &.warning {
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        }
        &.info {
          background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
        }
      }

      .stat-info {
        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #2d3748;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #718096;
          margin-top: 0.25rem;
        }
      }
    }
  }
}

// View Controls
.view-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  backdrop-filter: blur(10px);

  .results-info {
    .results-text {
      color: #4a5568;
      font-weight: 500;
    }
  }

  .view-toggle {
    .toggle-group {
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.5);

      .v-btn {
        border-radius: 12px !important;
        text-transform: none;
        font-weight: 500;
      }
    }
  }
}

// Loading State
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1rem;

  .loading-text {
    color: #718096;
    font-size: 1.1rem;
    margin: 0;
  }
}

// Grid View Styles
.volunteer-grid-container {
  .volunteer-grid {
    margin: 0 -8px;
  }

  .volunteer-card {
    border-radius: 20px;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    }

    .card-header {
      position: relative;
      height: 120px;

      .header-background {
        position: absolute;
        inset: 0;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }

      .header-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(365deg, #0a5b8acf 0%, #0d8abc57 100%);
      }

      .status-badge {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 2;

        .status-chip {
          backdrop-filter: blur(10px);
          font-size: 0.75rem;
          font-weight: 600;
        }
      }

      .avatar-container {
        position: absolute;
        bottom: -30px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 3;

        .profile-avatar {
          border: 4px solid white;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          border-radius: 50%;
        }

        .verification-badges {
          position: absolute;
          bottom: 8px;
          right: -8px;
          display: flex;
          gap: 2px;

          .verification-icon {
            background: white;
            border-radius: 50%;
            padding: 2px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
        }
      }
    }

    .volunteer-name {
      margin-top: 25px;
      justify-content: center;
      text-align: center;
      font-size: 1.2rem;
      font-weight: 600;
      color: #2d3748;
      padding: 16px 16px 8px;
    }

    .volunteer-contact {
      text-align: center;
      padding: 0 16px 16px;

      .contact-item {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-bottom: 0px;
        font-size: 0.875rem;
        color: #718096;
        margin-top: 11px;

        .contact-icon {
          color: #a0aec0;
        }

        .contact-text {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .card-body {
      flex: 1;
      padding: 16px;

      .stats-row {
        display: flex;
        justify-content: space-around;
        margin-bottom: 16px;
        padding: 16px 0;
        border-top: 1px solid #e2e8f0;
        border-bottom: 1px solid #e2e8f0;

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;

          .stat-icon {
            color: #0C84B5;
          }

          .stat-content {
            text-align: center;

            .stat-value {
              font-weight: 700;
              font-size: 1.25rem;
              color: #2d3748;
              line-height: 1;
            }

            .stat-label {
              font-size: 0.75rem;
              color: #a0aec0;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
          }
        }
      }

      .interests-section {
        .interests-label {
          font-size: 0.875rem;
          color: #4a5568;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .interests-group {
          margin: 0;
          justify-content: center;

          .interest-chip {
            margin: 2px;
            font-size: 0.75rem;
            height: 24px;
          }
        }
      }
    }

    .card-actions {
      border-top: 1px solid #e2e8f0;
      padding: 12px 16px;
      background: rgba(248, 250, 252, 0.8);

      .action-icon {
        width: 36px;
        height: 36px;
        margin-right: 4px;

        .v-icon {
          font-size: 18px;
        }
      }

      .profile-btn {
        text-transform: none;
        font-weight: 600;
        border-radius: 12px;
        height: 36px;
      }
    }
  }
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  backdrop-filter: blur(10px);

  .empty-title {
    color: #2d3748;
    margin: 1rem 0 0.5rem;
  }

  .empty-subtitle {
    color: #718096;
    margin-bottom: 2rem;
  }
}

// Table View Styles
.table-container {
  .volunteer-table {
    border-radius: 16px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);

    ::v-deep .v-data-table__wrapper {
      border-radius: 16px;
    }

    ::v-deep .v-data-table-header {
      background: rgba(102, 126, 234, 0.1);
    }

    ::v-deep .v-data-table tbody tr:hover {
      background: rgba(102, 126, 234, 0.05) !important;
    }

    .table-avatar {
      padding: 8px 0;
    }

    .volunteer-info {
      .volunteer-name-table {
        font-weight: 600;
        color: #2d3748;
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;

        .verification-indicators {
          display: flex;
          gap: 4px;
        }
      }

      .volunteer-contact-table {
        font-size: 0.875rem;
        color: #718096;

        div {
          margin-bottom: 2px;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .clickable-info {
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(112, 36, 196, 0.05);
        border-radius: 4px;
        padding: 4px 8px;
        margin: -4px -8px;
        
        .volunteer-name-table {
          color: #7024c4;
        }
      }
    }

    .hours-display {
      display: flex;
      align-items: center;
      font-weight: 500;
      color: #667eea;
    }

    .last-activity-display {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #2d3748;

      .activity-value {
        font-size: 1rem;
        color: #0a5b8a;
      }
    }

    .table-actions {
      display: flex;
      gap: 4px;
    }
  }
}

// Floating Action Button
.fab-button {
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  background: linear-gradient(135deg, #0a5b8a 0%, #0d8abc 100%);

  &:hover {
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
  }
}

// Responsive Design
@media (max-width: 1264px) {
  .management-header {
    .header-controls {
      .filter-group {
        flex-direction: column;
        width: 100%;

        .filter-select {
          min-width: unset;
          width: 100%;
        }
      }
    }
  }
}

@media (max-width: 960px) {
  .management-header {
    padding: 1.5rem 0;

    .header-content {
      gap: 1.5rem;

      .title-section .page-title {
        font-size: 2rem;
      }

      .header-controls {
        flex-direction: column;
        align-items: stretch;

        .search-container {
          min-width: unset;

          .search-field {
            width: 100%;
          }
        }

        .filter-group {
          flex-direction: row;
          gap: 0.5rem;

          .filter-select {
            flex: 1;
            min-width: 0;
          }
        }

        .action-buttons {
          justify-content: center;
        }
      }
    }
  }

  .view-controls {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .stats-overview {
    .stat-card .stat-content {
      padding: 1rem;

      .stat-icon {
        width: 50px;
        height: 50px;
      }

      .stat-info .stat-number {
        font-size: 1.5rem;
      }
    }
  }
}

@media (max-width: 600px) {
  .management-header {
    border-radius: 0 0 16px 16px;

    .header-content {
      .title-section .page-title {
        font-size: 1.75rem;
        flex-direction: column;
        gap: 0.5rem;
        text-align: center;
      }

      .header-controls .filter-group {
        flex-direction: column;
        gap: 0.5rem;
      }
    }
  }

  .volunteer-card {
    margin-bottom: 1rem;

    .card-header {
      height: 100px;
    }

    .volunteer-name {
      font-size: 1.1rem;
      margin-top: 35px;
    }

    .stats-row {
      .stat-item .stat-content {
        .stat-value {
          font-size: 1.1rem;
        }

        .stat-label {
          font-size: 0.7rem;
        }
      }
    }
  }

  .content-container {
    padding: 0 8px;
  }
}

// Animation keyframes
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.volunteer-card {
  animation: fadeInUp 0.6s ease-out;
}

// Custom scrollbar
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
}

// Updated styles for the card body section
.card-body {
  flex: 1;
  padding: 16px;

  .stats-row {
    display: flex;
    justify-content: space-around;
    margin-bottom: 20px;
    padding: 16px 0;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;

    .stat-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      .stat-icon {
        color: #667eea;
      }

      .stat-content {
        text-align: center;

        .stat-value {
          font-weight: 700;
          font-size: 1.25rem;
          color: #2d3748;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #a0aec0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  // New styles for tags container
  .tags-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 120px; // Ensure consistent card height
  }

  .tag-section {
    background: rgba(248, 250, 252, 0.8);
    border-radius: 12px;
    padding: 12px;
    border-left: 4px solid transparent;
    transition: all 0.3s ease;

    &.interests-section {
      border-left-color: #9f7aea; // Purple accent
      background: linear-gradient(
        135deg,
        rgba(159, 122, 234, 0.08) 0%,
        rgba(139, 92, 246, 0.05) 100%
      );

      &:hover {
        background: linear-gradient(
          135deg,
          rgba(159, 122, 234, 0.12) 0%,
          rgba(139, 92, 246, 0.08) 100%
        );
        transform: translateX(2px);
      }
    }

    &.causes-section {
      border-left-color: #38b2ac; // Teal accent
      background: linear-gradient(
        135deg,
        rgba(56, 178, 172, 0.08) 0%,
        rgba(49, 151, 149, 0.05) 100%
      );

      &:hover {
        background: linear-gradient(
          135deg,
          rgba(56, 178, 172, 0.12) 0%,
          rgba(49, 151, 149, 0.08) 100%
        );
        transform: translateX(2px);
      }
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;

      .section-icon {
        &.interests-icon {
          color: #9f7aea;
          background: rgba(159, 122, 234, 0.1);
          border-radius: 50%;
          padding: 4px;
        }

        &.causes-icon {
          color: #38b2ac;
          background: rgba(56, 178, 172, 0.1);
          border-radius: 50%;
          padding: 4px;
        }
      }

      .section-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: #4a5568;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }

    .tag-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;

      .interest-tag {
        font-size: 0.75rem;
        font-weight: 500;
        height: 24px;
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(159, 122, 234, 0.3);
        }

        &.overflow-tag {
          font-style: italic;
          opacity: 0.8;
        }
      }

      .cause-tag {
        font-size: 0.75rem;
        font-weight: 500;
        height: 24px;
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(56, 178, 172, 0.3);
        }

        &.overflow-tag {
          font-style: italic;
          opacity: 0.9;
        }
      }
    }
  }

  .empty-tags {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 20px;
    background: rgba(247, 250, 252, 0.5);
    border-radius: 12px;
    border: 2px dashed #e2e8f0;

    .empty-text {
      font-size: 0.875rem;
      color: #a0aec0;
      font-style: italic;
    }
  }
}

// Responsive adjustments
@media (max-width: 600px) {
  .card-body {
    .tags-container {
      gap: 12px;
      min-height: 100px;
    }

    .tag-section {
      padding: 10px;

      .section-header {
        .section-title {
          font-size: 0.8rem;
        }
      }

      .tag-chips {
        gap: 4px;

        .interest-tag,
        .cause-tag {
          font-size: 0.7rem;
          height: 22px;
        }
      }
    }
  }
}
</style>





